import { useBreakpoint } from 'hooks';
import { useEffect, useState } from 'react';
import { memoize } from 'lodash';
import { BB_ASSUME_INPUT_CLASS } from '../../../components/AssumeNavbar/BBAssumeNavbar';
import { ASSUME_BAR_ID } from '../../../components/AssumeNavbar';
import { TOURS_MAP_ID, TOURS_NAME_INPUT_ID } from '../constants';

// wait for an element to exists.
const waitForElm = (selector: string): Promise<Element> => new Promise(resolve => {
    let elem = document.querySelector(selector);
    if (elem) return resolve(elem);

    const observer = new MutationObserver(() => {
      elem = document.querySelector(selector);
      if (elem) {
        resolve(elem);
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });

// elements that should not trigger scroll no matter what
// the assume bar && the map should not trigger scroll
const BLACK_LIST: (() => Promise<HTMLElement>)[] = [
  async () => (await waitForElm(`#${TOURS_MAP_ID}`)).parentElement,
  async () => (await waitForElm(`#${ASSUME_BAR_ID}`)) as HTMLElement,
];

// used to prevent horizontal touch move triggering entire page scroll
// returns the direction of the touch gesture.
const getTouchDirection = (e: TouchEvent) => {
  const { prevX, prevY } = getTouchDirection as any;
  const { clientX, clientY } = e.touches[0];
  const deltaX: number = prevX ? Math.abs(prevX - clientX) : 0;
  const deltaY: number = prevY ? Math.abs(prevY - clientY) : 0;
  Object.assign(getTouchDirection, { prevX: clientX, prevY: clientY });

  return {
    horizontal: deltaX > deltaY,
    vertical: deltaY > deltaX,
  };
};

// for most of the time, we don't want horizontal scroll
// since a horizontal scroll could still vertically move the entire page
// -> messing up the layout when the virtual keyboard is open on iOS safari
// But for elements with their own scroll container -> it should be fine.
const allowHorizontalScrollForElement = memoize((element: any) => {
  if (!(element instanceof HTMLElement)) return false;
  let allowHorizontalScroll = false;
  let currentElement: HTMLElement | null = element;
  do {
    if (
      currentElement.className.includes('ant-table-row') ||
      currentElement.className.includes('cross-org')
    ) {
      allowHorizontalScroll = true;
    }
    currentElement = currentElement.parentElement;
  } while (currentElement instanceof HTMLElement && !allowHorizontalScroll);

  return allowHorizontalScroll;
});

const useStopUnnecessaryBodyScroll = (isDesktop: boolean) => {
  useEffect(() => {
    if (isDesktop) return;

    const cancelScroll = (e: TouchEvent) => {
      const { horizontal } = getTouchDirection(e);

      if (horizontal && !allowHorizontalScrollForElement(e.target))
        e.preventDefault();

      // also stop scroll when certain input elements are focused:
      if (
        document.activeElement.className === BB_ASSUME_INPUT_CLASS ||
        document.activeElement.id === TOURS_NAME_INPUT_ID
      ) {
        e.preventDefault();
      }
    };
    document.body.addEventListener('touchmove', cancelScroll, {
      passive: false,
    });
    return () => {
      document.body.removeEventListener('touchmove', cancelScroll);
    };
  }, [isDesktop]);
};

// Disable certain elements from triggering the entire page scrolling
const useTouchScrollBlackList = (isDesktop: boolean) => {
  useEffect(() => {
    if (isDesktop) return;

    let elements: HTMLElement[];

    const cancelScroll = (e: TouchEvent) => {
      e.preventDefault();
    };

    (async () => {
      const elementsToBlock = await Promise.all(
        BLACK_LIST.map(async fn => fn()),
      );
      elementsToBlock.forEach(element => {
        element.addEventListener('touchmove', cancelScroll, {
          passive: false,
        });
      });
      elements = elementsToBlock;
    })();
    return () => {
      elements?.forEach(element => {
        element.removeEventListener('touchmove', cancelScroll);
      });
    };
  }, [isDesktop]);
};

// this ensures the layout doesn't get messed up on mobile during initial loading of the page when:
// the virtual keyboard is open && the page is half-way scrolled
// Happens on iOS Safari only 😡
const useDelayRender = (isDesktop: boolean) => {
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    if (!isDesktop) setTimeout(() => setIsReady(true), 0);
  }, [isDesktop]);

  return isDesktop || isReady;
};

const useFixMobile = () => {
  const { md } = useBreakpoint();
  const [initialized, setInitialized] = useState(false);
  const isDesktop = md && initialized;
  useEffect(() => setInitialized(true), []);

  // prevent scroll triggering when either:
  // 1. certain elements are focused, or 2. scroll direction is horizontal.
  // since on iOS, the whole page became scrollable when the virtual keyboard is open -> layout messed up.
  useStopUnnecessaryBodyScroll(isDesktop);

  // prevent certain element from triggering scroll at all
  useTouchScrollBlackList(isDesktop);

  // avoid race condition with iOS safari.
  const ready = useDelayRender(isDesktop);
  return ready;
};

export default useFixMobile;
